import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "../../ui/drawer";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { X, CheckCircle2, AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";

const ParkingFormModal = ({ isOpen, onClose, onSubmit, spot = null, users = [], allSpots = [] }) => {
  const { t } = useTranslation();
  const isEditing = !!spot;
  
  const [formData, setFormData] = useState({
    spot_number: "",
    column_number: "",
    row_number: "",
    floor: "1",
    type: "SPOT",
    status: "none",
    owner_id: "none"
  });
  
  const [error, setError] = useState("");
  const [gridSize, setGridSize] = useState({ rows: 10, cols: 10 });
  const [occupiedPositions, setOccupiedPositions] = useState([]);
  const [occupiedSpotsInfo, setOccupiedSpotsInfo] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [activeTab, setActiveTab] = useState("details");
  const [zoomLevel, setZoomLevel] = useState(1);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      // On smaller screens, adjust the zoom level for the grid
      setZoomLevel(window.innerWidth < 500 ? 0.9 : 1);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // When editing, populate form with spot data
  useEffect(() => {
    if (isEditing && spot) {
      // Make sure values are converted to strings for the Select components
      setFormData({
        spot_number: spot.spot_number || "",
        column_number: String(spot.column_number || ""),
        row_number: String(spot.row_number || ""),
        floor: String(spot.floor || "1"),
        type: spot.type || "SPOT",
        status: spot.status || "none",
        owner_id: spot.owner_id || "none"
      });
    } else {
      // Reset form when adding a new spot
      setFormData({
        spot_number: "",
        column_number: "",
        row_number: "",
        floor: "1", 
        type: "SPOT",
        status: "none",
        owner_id: "none"
      });
    }
    
    // Clear any previous errors
    setError("");
  }, [isEditing, spot, isOpen]);

  // Update occupied positions whenever allSpots or floor changes
  useEffect(() => {
    const currentFloorSpots = allSpots.filter(s => 
      String(s.floor) === formData.floor && 
      (!isEditing || (isEditing && s.id !== spot?.id))
    );
    
    setOccupiedPositions(
      currentFloorSpots.map(s => ({
        row: parseInt(s.row_number),
        col: parseInt(s.column_number)
      }))
    );

    // Store spot numbers for occupied spots
    const spotInfo = {};
    currentFloorSpots.forEach(s => {
      const key = `${s.row_number}-${s.column_number}`;
      spotInfo[key] = s.spot_number;
    });
    setOccupiedSpotsInfo(spotInfo);
    
    // Calculate grid size based on existing spots
    if (currentFloorSpots.length > 0) {
      const maxRow = Math.max(...currentFloorSpots.map(s => parseInt(s.row_number) || 0), 10);
      const maxCol = Math.max(...currentFloorSpots.map(s => parseInt(s.column_number) || 0), 10);
      setGridSize({ 
        rows: Math.max(maxRow + 3, 10), 
        cols: Math.max(maxCol + 3, 10) 
      });
    } else {
      setGridSize({ rows: 10, cols: 10 });
    }
  }, [allSpots, formData.floor, isEditing, spot]);

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.spot_number || !formData.column_number || !formData.row_number) {
      setError(t('management.parking.form.validation'));
      return;
    }
    
    try {
      // Convert back from "none" to null before submitting
      const processedData = {
        ...formData,
        status: formData.status === "none" ? null : formData.status,
        owner_id: formData.owner_id === "none" ? null : formData.owner_id
      };
      
      // If editing, pass the spot ID
      const submitData = isEditing ? { id: spot.id, ...processedData } : processedData;
      await onSubmit(submitData);
      setError("");
      onClose();
    } catch (error) {
      setError(t('management.parking.form.error', { message: error.message || t('parking.unknown') }));
    }
  };

  const handleGridClick = (row, col) => {
    // Check if position is already occupied
    const isOccupied = occupiedPositions.some(
      p => p.row === row && p.col === col
    );
    
    if (!isOccupied) {
      setFormData({
        ...formData,
        row_number: String(row),
        column_number: String(col)
      });
      
      // On mobile, switch to details tab after selection
      if (isMobile) {
        setActiveTab("details");
      }
    }
  };

  // Check if a position is occupied
  const isPositionOccupied = (row, col) => {
    return occupiedPositions.some(
      p => p.row === row && p.col === col
    );
  };

  // Get spot number for occupied position
  const getSpotNumberForPosition = (row, col) => {
    return occupiedSpotsInfo[`${row}-${col}`] || '';
  };

  // Check if a position is currently selected
  const isPositionSelected = (row, col) => {
    return parseInt(formData.row_number) === row && 
           parseInt(formData.column_number) === col;
  };

  // Scroll to selected position useEffect
  useEffect(() => {
    if (formData.row_number && formData.column_number) {
      const selectedElement = document.getElementById(`cell-${formData.row_number}-${formData.column_number}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center', 
          inline: 'center' 
        });
      }
    }
  }, [formData.row_number, formData.column_number]);

  const renderParkingGrid = () => {
    const rows = [];
    
    // Cell size classes - smaller on mobile
    const cellSizeClasses = isMobile 
      ? "w-6 h-6 text-[10px]" 
      : "w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-xs";
    
    // Row header size classes - smaller on mobile
    const rowHeaderClasses = isMobile
      ? "w-5 h-6 text-[10px]"
      : "w-7 h-8 sm:w-8 sm:h-9 md:w-9 md:h-10 text-xs";
      
    // Column header size classes - smaller on mobile
    const colHeaderClasses = isMobile
      ? "w-7 h-7 text-[10px]"
      : "w-9 h-7 sm:w-10 sm:h-8 md:w-11 md:h-9 text-xs";
    
    for (let i = 1; i <= gridSize.rows; i++) {
      const cells = [];
      for (let j = 1; j <= gridSize.cols; j++) {
        const isOccupied = isPositionOccupied(i, j);
        const isSelected = isPositionSelected(i, j);
        const spotNumber = getSpotNumberForPosition(i, j);
        
        cells.push(
          <div
            id={`cell-${i}-${j}`}
            key={`${i}-${j}`}
            onClick={() => handleGridClick(i, j)}
            className={`${cellSizeClasses} border m-[2px] cursor-pointer flex items-center justify-center rounded-md transition-all ${
              isOccupied 
                ? 'bg-red-50 border-red-300 dark:bg-red-900/50 dark:border-red-800 cursor-not-allowed' 
                : isSelected
                  ? 'bg-green-100 border-green-400 dark:bg-green-900/50 dark:border-green-700 shadow-sm' 
                  : 'hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/30 dark:hover:border-blue-800'
            }`}
            title={isOccupied 
              ? t('management.parking.form.grid.occupiedTooltip', { spotNumber }) 
              : t('management.parking.form.grid.positionTooltip', { row: i, col: j })}
          >
            {isOccupied ? 
              (spotNumber?.length <= 3 ? spotNumber : '') : 
              (isSelected ? <CheckCircle2 size={isMobile ? 12 : 16} className="text-green-600 dark:text-green-400" /> : '')}
          </div>
        );
      }
      
      rows.push(
        <div key={i} className="flex">
          <div className={`${rowHeaderClasses} flex items-center justify-center font-medium text-gray-500 dark:text-gray-400 sticky left-0 bg-gray-50 dark:bg-gray-900/70 z-10`}>
            {i}
          </div>
          <div className="flex">
            {cells}
          </div>
        </div>
      );
    }
    
    // Add column headers - make them sticky
    const colHeaders = [];
    colHeaders.push(
      <div key="empty" className={`${isMobile ? 'w-5 h-5' : 'w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9'} sticky left-0 top-0 z-20 bg-gray-100 dark:bg-gray-800`}></div>
    );
    
    for (let j = 1; j <= gridSize.cols; j++) {
      colHeaders.push(
        <div key={j} className={`${colHeaderClasses} flex items-center justify-center font-medium text-gray-500 dark:text-gray-400 sticky top-0 bg-gray-100 dark:bg-gray-800 z-10`}>
          {j}
        </div>
      );
    }
    
    return (
      <div className="p-2 md:p-4">
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-1">
            {t('management.parking.form.grid.title', { number: formData.floor })}
          </h3>
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-100 border border-green-400 dark:bg-green-900/50 dark:border-green-700 mr-1"></div>
              <span>{t('management.parking.form.grid.selected')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-100 border border-red-300 dark:bg-red-900/50 dark:border-red-800 mr-1"></div>
              <span>{t('management.parking.form.grid.occupied')}</span>
            </div>
          </div>
          <p className="text-xs italic mt-2 text-neutral-500 dark:text-neutral-400">
            {isMobile 
              ? t('management.parking.form.grid.mobileHint')
              : t('management.parking.form.grid.desktopHint')
            }
          </p>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg shadow-inner">
          {/* Fixed height container with overflow - shorter on mobile */}
          <div className={`${isMobile ? 'h-[300px]' : 'h-[350px]'} overflow-auto`} 
               style={{ maxWidth: isMobile ? '80vw' : '600px' }}>
            <div className="sticky top-0 z-10 flex" style={{ minWidth: 'fit-content' }}>
              {colHeaders}
            </div>
            <div style={{ minWidth: 'fit-content' }}>
              {rows}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="space-y-4">
      {error && (
        <div className="text-sm font-medium text-red-500 bg-red-50 p-3 rounded-md dark:bg-red-900/20 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="spot_number">
          {t('management.parking.form.spotNumber')} <span className="text-red-500">*</span>
        </label>
        <Input
          id="spot_number"
          value={formData.spot_number}
          onChange={(e) => setFormData({...formData, spot_number: e.target.value})}
          placeholder="P01"
          className="shadow-sm h-10"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="column_number">
            {t('management.parking.form.column')} <span className="text-red-500">*</span>
          </label>
          <Input
            id="column_number"
            type="number"
            value={formData.column_number}
            onChange={(e) => setFormData({...formData, column_number: e.target.value})}
            placeholder="1"
            className="shadow-sm h-10"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="row_number">
            {t('management.parking.form.row')} <span className="text-red-500">*</span>
          </label>
          <Input
            id="row_number"
            type="number"
            value={formData.row_number}
            onChange={(e) => setFormData({...formData, row_number: e.target.value})}
            placeholder="1"
            className="shadow-sm h-10"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="floor">
          {t('management.parking.form.floor')}
        </label>
        <Select 
          value={formData.floor} 
          onValueChange={(value) => setFormData({...formData, floor: value})}
        >
          <SelectTrigger id="floor" className="w-full shadow-sm h-10">
            <SelectValue>
              {t('management.parking.form.floorNumber', { number: formData.floor })}
            </SelectValue>
          </SelectTrigger>
          <SelectContent position={isMobile ? "popper" : "item-aligned"}>
            <SelectItem value="1">{t('management.parking.form.floorNumber', { number: 1 })}</SelectItem>
            <SelectItem value="2">{t('management.parking.form.floorNumber', { number: 2 })}</SelectItem>
            <SelectItem value="3">{t('management.parking.form.floorNumber', { number: 3 })}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="type">
          {t('management.parking.form.type')}
        </label>
        <Select 
          value={formData.type} 
          onValueChange={(value) => setFormData({...formData, type: value})}
        >
          <SelectTrigger id="type" className="w-full shadow-sm h-10">
            <SelectValue>
              {formData.type === "SPOT" 
                ? t('management.parking.form.types.spot')
                : t('management.parking.form.types.garage')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent position={isMobile ? "popper" : "item-aligned"}>
            <SelectItem value="SPOT">{t('management.parking.form.types.spot')}</SelectItem>
            <SelectItem value="GARAGE">{t('management.parking.form.types.garage')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="status">
          {t('management.parking.form.statuss')}
        </label>
        <Select 
          value={formData.status} 
          onValueChange={(value) => setFormData({...formData, status: value})}
        >
          <SelectTrigger id="status" className="w-full shadow-sm h-10">
            <SelectValue placeholder={t('management.parking.form.statuss')} />
          </SelectTrigger>
          <SelectContent position={isMobile ? "popper" : "item-aligned"}>
            <SelectItem value="none">{t('management.parking.form.status.closed')}</SelectItem>
            <SelectItem value="available">{t('management.parking.form.status.available')}</SelectItem>
            <SelectItem value="occupied">{t('management.parking.form.status.occupied')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="owner_id">
          {t('management.parking.form.owner')}
        </label>
        <Select 
          value={formData.owner_id} 
          onValueChange={(value) => setFormData({...formData, owner_id: value})}
        >
          <SelectTrigger id="owner_id" className="w-full shadow-sm h-10">
            <SelectValue placeholder={t('management.parking.form.owners.unassigned')} />
          </SelectTrigger>
          <SelectContent position={isMobile ? "popper" : "item-aligned"}>
            <SelectItem value="none">{t('management.parking.form.owners.unassigned')}</SelectItem>
            {users.map(user => (
              <SelectItem key={user.id} value={user.id}>
                {user.username}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  // Create the content component used by both mobile and desktop versions
  const ModalContent = () => (
    <>
      {isMobile ? (
        // Mobile view with tabs
        <Tabs defaultValue="details" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="details">{t('management.parking.form.tabs.details')}</TabsTrigger>
            <TabsTrigger value="grid">{t('management.parking.form.tabs.grid')}</TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="mt-0">
            {renderForm()}
          </TabsContent>
          <TabsContent value="grid" className="mt-0 overflow-visible">
            <div className="border rounded-lg shadow-sm bg-white dark:bg-neutral-900 overflow-hidden">
              {renderParkingGrid()}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        // Desktop view with columns
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Form Column */}
          <div className="space-y-4 lg:w-1/3">
            {renderForm()}
          </div>
          
          {/* Parking grid visualization */}
          <div className="lg:w-2/3 border rounded-lg shadow-sm bg-white dark:bg-neutral-900 overflow-hidden">
            {renderParkingGrid()}
          </div>
        </div>
      )}
    </>
  );

  // Mobile drawer version
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="px-4 pt-4 pb-6 max-h-[90vh] overflow-y-auto">
          <DrawerHeader className="p-0 mb-4">
            <DrawerTitle className="text-lg font-semibold">
              {isEditing 
                ? t('management.parking.form.editTitle') 
                : t('management.parking.form.addTitle')}
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="absolute right-2 top-2 h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          
          <ModalContent />
          
          <DrawerFooter className="px-0 pt-6">
            <Button 
              onClick={handleSubmit}
              className="bg-primary text-white hover:bg-primary/90 w-full h-10"
              disabled={!formData.spot_number || !formData.column_number || !formData.row_number}
            >
              {isEditing 
                ? t('management.parking.form.actions.saveChanges') 
                : t('management.parking.form.actions.addSpot')}
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full h-10"
            >
              {t('management.parking.form.actions.cancel')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop dialog version
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[900px] w-[95vw] max-h-[90vh] p-4 sm:p-6 rounded-lg overflow-y-auto">
        <DialogHeader className="mb-4 relative">
          <DialogTitle className="text-lg sm:text-xl pr-8">
            {isEditing 
              ? t('management.parking.form.editTitle') 
              : t('management.parking.form.addTitle')}
          </DialogTitle>
          <button 
            onClick={onClose}
            className="absolute right-0 top-0 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label={t('management.parking.form.actions.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <ModalContent />

        <DialogFooter className="mt-6 flex-col sm:flex-row gap-3 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full sm:w-auto h-10"
          >
            {t('management.parking.form.actions.cancel')}
          </Button>
          <Button 
            className="bg-primary text-white hover:bg-primary/90 w-full sm:w-auto h-10"
            onClick={handleSubmit}
            disabled={!formData.spot_number || !formData.column_number || !formData.row_number}
          >
            {isEditing 
              ? t('management.parking.form.actions.saveChanges') 
              : t('management.parking.form.actions.addSpot')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ParkingFormModal;